import { EgmCardStatus, useWealthOverview } from '@egw/api'
import { Button, GenericError, ModalDrawer, PageHeader } from '@egw/components'
import { useIsMobileScreen } from '@egw/components/src/popover/hooks/layout-hook'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { type FC, useEffect, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { sendEvent } from '@egw/events'
import CardFront from '../../components/card-front/card-front'
import { SetPin } from '../../components/set-pin-attribute/set-pin'

export interface DebitCardSetPinPageProps {
  alias: string
}

export const DebitCardSetPin: FC<DebitCardSetPinPageProps> = ({ alias }) => {
  const { t } = useTranslation('features-account')
  const isMobileScreen = useIsMobileScreen()
  const navigate = useNavigate()

  const [showSetPinModal, setShowSetPinModal] = useState<boolean>(false)

  const { data: wealthOverviewData, isLoading, error } = useWealthOverview()

  const account = wealthOverviewData?.highYieldChecking?.accounts.find((curr) => curr.alias === parseInt(alias))

  const debitCard = account?.debitCard[0]
  const needsPin = debitCard?.needToSetPin
  const cardIsActivated = debitCard?.cardStatus === EgmCardStatus.ACTIVE
  const cardId = debitCard?.cardId ?? ''

  useEffect(() => {
    if (cardIsActivated && !needsPin && !showSetPinModal) {
      void navigate(WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_ACTIVATE_SUCCESS.path.replace(':alias', alias.toString()))
    }
  }, [navigate, alias, showSetPinModal, cardIsActivated, needsPin])

  return (
    <>
      {isLoading ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <div className="egw-container">
            <Skeleton count={1} height="60px" />
          </div>
          <Skeleton count={1} height="270px" />
          <div className="egw-container">
            <Skeleton count={5} height="40px" className="mt-1" />
          </div>
        </SkeletonTheme>
      ) : null}

      {!isLoading ? (
        <PageHeader
          title={t('debit_card.set_pin_title')}
          subtitle={t('debit_card.title')}
          backLinkUrl={
            WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_SET_PIN.backPath?.replace(':alias', alias.toString()) ?? ''
          }
        />
      ) : null}

      {debitCard ? (
        <>
          <div className="default-page-width-wrapper py-8 pb-12 md:bg-gray-50">
            <div className="default-page-width flex flex-col align-center justify-center items-center">
              <div className="justify-center align-center flex flex-col items-center px-3">
                <CardFront className="opacity-40 max-w-[340px]" width={isMobileScreen ? '100%' : 340} />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="justify-center align-center flex flex-col items-center shadow rounded p-6 -mt-6 mx-[10%] sm:mx-0 bg-white sm:w-[340px] max-w-[340px]">
              <p>{t('debit_card.activate.activated_please_set_pin')}</p>
              <Button
                id="activateCard"
                className="mb-4 md:mb-0 w-full mt-6"
                onClick={() => {
                  sendEvent('user_changePIN_click', {
                    eventSource: 'egWealth',
                    category: 'card-activation-pin',
                    subCategory: 'intent',
                  })
                  setShowSetPinModal(true)
                }}
              >
                {t('debit_card.set_pin_title')}
              </Button>
            </div>
          </div>
          <ModalDrawer
            isOpen={showSetPinModal}
            onClose={() => {
              setShowSetPinModal(false)
            }}
            title={t('debit_card.set_pin_title')}
          >
            <SetPin
              isOpen={showSetPinModal}
              alias={alias}
              closeModal={() => {
                setShowSetPinModal(false)
              }}
              cardId={cardId}
            />
          </ModalDrawer>
        </>
      ) : null}

      {Boolean(error) && (
        <div className="egw-container">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      )}
    </>
  )
}
